import { AppContext } from "01Data/AppData";
import { useMessageDialog } from "97Parts/useMessageDialog";
import { Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { validate } from "email-validator";
import { FC, useContext, useState } from "react";

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
  margin: "0.2rem",
}));

type DialogResult = "ok" | "cancel";

type DialogProps = {
  open: boolean;
  onClose: (result: DialogResult) => void;
};

const UpdateMailDialogContent: FC<DialogProps> = ({ open, onClose }) => {
  const account = useContext(AppContext).account;
  const [mail, setMail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [dialogMessage, setDialogMessage] = useState("");
  const { MessageDialog, openMessageDialog } = useMessageDialog();

  const handleSend = async () => {
    try {
      if (!validate(mail)) {
        setErrorMessage("有効なメールアドレスを入力してください。");
        return;
      }
      const result = await account.changeMail(mail);
      // console.log(result);
      if (result !== "SUCCESS") {
        setErrorMessage(`メールアドレスの変更が失敗しました。`);
      } else {
        setDialogMessage("認証コードを送信しました。");
        await openMessageDialog();
      }
    } catch (error: any) {
      setErrorMessage(`メールアドレスの変更が失敗しました。\n${error.message}`);
    }
  };

  const handleApply = async () => {
    try {
      const result = await account.verifyChangeMailCode(verifyCode);
      // console.log(result);
      if (result !== "SUCCESS") {
        setErrorMessage(`認証コードの確認が失敗しました。`);
      } else {
        onClose("ok");
      }
    } catch (error: any) {
      setErrorMessage(`認証コードの確認が失敗しました。\n${error.message}`);
    }
  };

  const handleClose = (event: {}, reason: "backdropClick") => {
    if (reason !== "backdropClick") {
      onClose("cancel");
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="update-mail-dialog-title"
      aria-describedby="update-mail-dialog-description"
    >
      <DialogTitle id="update-mail-dialog-title">
        メールアドレス変更
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="update-mail-dialog-description">
          新しいメールアドレスを入力してください。入力されたアドレスに認証コードを送信します。
        </DialogContentText>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            autoFocus
            fullWidth
            required
            type="text"
            variant="filled"
            margin="normal"
            id="mail"
            label="メールアドレス"
            name="mail"
            inputProps={{ maxLength: 128 }}
            value={mail}
            onChange={(e) => {
              setErrorMessage("");
              setMail(e.target.value);
            }}
          />
          <Button
            variant="contained"
            size="medium"
            onClick={handleSend}
            sx={{ margin: "8px", minWidth: "88px" }}
            endIcon={<Send />}
          >
            送信
          </Button>
        </Box>
        <DialogContentText>
          届いたメールを確認し、認証コードを入力してください。
        </DialogContentText>
        <TextField
          required
          type="text"
          inputProps={{ inputMode: "numeric", maxLength: 128 }}
          variant="filled"
          margin="normal"
          id="verifyCode"
          label="コードを入力"
          name="verifyCode"
          onChange={(e) => {
            setErrorMessage("");
            setVerifyCode(e.target.value);
          }}
          value={verifyCode}
        />
        <ErrorText>{errorMessage ? errorMessage : "　"}</ErrorText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleApply}>更新する</Button>
        <Button onClick={() => onClose("cancel")}>ユーザー設定に戻る</Button>
      </DialogActions>
      <MessageDialog message={dialogMessage} />
    </Dialog>
  );
};

export const useUpdateMailDialog = () => {
  const [open, setOpen] = useState(false);
  const [resolve, setResolve] = useState<(value: DialogResult) => void>();

  const openUpdateMailDialog = () => {
    setOpen(true);
    return new Promise<DialogResult>((resolve) => {
      setResolve(() => resolve);
    });
  };

  const onClose = (result: DialogResult) => {
    setOpen(false);
    if (resolve) {
      resolve(result);
    }
  };

  const UpdateMailDialog = () => (
    <UpdateMailDialogContent open={open} onClose={onClose} />
  );

  return {
    UpdateMailDialog,
    openUpdateMailDialog,
  };
};
